import {
  useState,
  type Dispatch,
  type ReactElement,
  type RefObject,
  type SetStateAction,
} from 'react';
import { useTranslation } from 'react-i18next';

import { useEditableDraft } from '@/features/patient-record/hooks/useEditableDraft';
import { useDirtyDialogGuard } from '@/features/patient-record/hooks/useDirtyDialogGuard';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  useCreateTestOrder,
  useUpdateTestOrder,
  type TestOrderInput,
} from '@/features/patient-record/hooks/useTestOrderMutations';
import type { TestOrderViewModel } from '@/features/patient-record/types';

import { useFocusOnMount } from '../_shared/useFocusOnMount';

import { TestOrderFormFields } from './TestOrderFormFields';

interface AddProps {
  readonly guid: string;
  readonly mode: 'add';
  readonly onClose: () => void;
  readonly order?: never;
}

interface EditProps {
  readonly guid: string;
  readonly mode: 'edit';
  readonly order: TestOrderViewModel;
  readonly onClose: () => void;
}

type TestOrderFormProps = AddProps | EditProps;

function buildInitial(props: TestOrderFormProps): TestOrderInput {
  if (props.mode === 'edit') {
    return {
      testName: props.order.testName,
      testType: props.order.testType,
      testCode: props.order.testCode,
      panelName: props.order.panelName,
      labProvider: props.order.labProvider,
      indication: props.order.indication,
      orderedByExternal: props.order.orderedByExternal,
      orderedDate: props.order.orderedDate,
      sampleType: props.order.sampleType,
      sampleCollectedDate: props.order.sampleCollectedDate,
      sampleSentDate: props.order.sampleSentDate,
      targetDeliveryDate: props.order.targetDeliveryDate,
      specimen: props.order.specimen,
      status: props.order.status,
      resultDate: props.order.resultDate,
      resultSummary: props.order.resultSummary,
      externalReportUrl: props.order.externalReportUrl,
      notes: props.order.notes,
    };
  }
  return {
    testName: '',
    testType: 'GENE_PANEL',
    testCode: null,
    panelName: null,
    labProvider: null,
    indication: null,
    orderedByExternal: null,
    orderedDate: new Date().toISOString().slice(0, 10),
    sampleType: null,
    sampleCollectedDate: null,
    sampleSentDate: null,
    targetDeliveryDate: null,
    specimen: null,
    status: 'ORDERED',
    resultDate: null,
    resultSummary: null,
    externalReportUrl: null,
    notes: null,
  };
}

interface TestOrderFormState {
  readonly form: TestOrderInput;
  readonly setForm: Dispatch<SetStateAction<TestOrderInput>>;
  readonly pending: boolean;
  readonly canSubmit: boolean;
  readonly dirty: boolean;
  readonly submit: () => void;
  /** Attach to the form wrapper so focus-within (isEditing) + reseed-safety work (PTV2-1327). */
  readonly containerRef: (node: HTMLElement | null) => void;
}

/**
 * Owns the test-order draft + submit for both mounts (inline add card / edit
 * dialog). Kept separate from the guard wiring so each mount can attach the
 * right unsaved-data guard — `useDirtyRegistration` for the inline card,
 * `useDirtyDialogGuard` for the modal — without a conditional hook call.
 */
function useTestOrderFormState(props: TestOrderFormProps): TestOrderFormState {
  const createMutation = useCreateTestOrder(props.guid);
  const updateMutation = useUpdateTestOrder(props.guid);
  // Captured once on mount (the add card has no server source; the edit dialog's order is fixed for
  // the modal's lifetime) — matches the previous snapshot-on-mount behaviour.
  const [initial] = useState(() => buildInitial(props));

  const commit = (f: TestOrderInput): Promise<unknown> =>
    props.mode === 'add'
      ? createMutation.mutateAsync(f)
      : updateMutation.mutateAsync({ id: props.order.id, input: f });

  // PTV2-1327: the primitive owns the draft + auto-registration (replacing the inline card's
  // useDirtyRegistration and feeding the dialog's guard). Explicit-submit (autosave:false): both
  // mounts commit on the button / confirm prompt, never on idle.
  const form = useEditableDraft<TestOrderInput>({
    initial,
    label: 'Test order',
    autosave: false,
    canSave: (f) =>
      f.testName.trim().length > 0 &&
      f.orderedDate.length > 0 &&
      (f.status !== 'RESULTED' ||
        (f.resultSummary ?? '').trim().length > 0 ||
        (f.externalReportUrl ?? '').trim().length > 0),
    onSave: (f) => commit(f),
  });

  const pending = createMutation.isPending || updateMutation.isPending;
  const canSubmit = form.canSave && !pending;

  // Explicit submit commits AND closes (the button, the form onSubmit, the confirm-prompt Save). The
  // primitive's own onSave (commit only, no close) is what the nav-guard/registry flush calls.
  const submit = (): void => {
    if (!form.canSave || pending) return;
    void commit(form.draft).then(props.onClose).catch(() => undefined);
  };

  return {
    form: form.draft,
    setForm: form.setDraft,
    pending,
    canSubmit,
    dirty: form.isDirty,
    submit,
    containerRef: form.containerProps.ref,
  };
}

interface TestOrderFormViewProps {
  readonly state: TestOrderFormState;
  readonly focusRef: RefObject<HTMLInputElement | null>;
  readonly lockStatusToCancelled: boolean;
  readonly submitLabel: string;
  /** Close/discard — the dialog passes the guard's requestClose. */
  readonly onClose: () => void;
}

/** Presentational fields + footer, shared by the add and edit dialogs. Holds no guard state. */
function TestOrderFormView({
  state,
  focusRef,
  lockStatusToCancelled,
  submitLabel,
  onClose,
}: TestOrderFormViewProps): ReactElement {
  const { t } = useTranslation();
  const { form, setForm, pending, canSubmit, submit, containerRef } = state;

  return (
    <form
      ref={containerRef}
      className="flex flex-col min-h-0 flex-1"
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4">
        <TestOrderFormFields
          form={form}
          setForm={setForm}
          focusRef={focusRef}
          lockStatusToCancelled={lockStatusToCancelled}
        />
      </div>
      <div className="flex items-center justify-end gap-2 px-6 py-3 border-t border-border bg-background">
        <Button type="button" variant="ghost" onClick={onClose} disabled={pending}>
          {t('ui.dialog.close')}
        </Button>
        <Button
          type="submit"
          disabled={!canSubmit}
          loading={pending}
          loadingText={t('patient.dirtyFooter.saving')}
        >
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}

type TestOrderFormDialogProps =
  | {
      readonly guid: string;
      readonly mode: 'add';
      readonly order?: never;
      readonly onClose: () => void;
    }
  | {
      readonly guid: string;
      readonly mode: 'edit';
      readonly order: TestOrderViewModel;
      readonly onClose: () => void;
    };

/**
 * Add / edit-order modal. A wide, two-column dialog so the ~15 fields fit
 * without scrolling on a typical viewport (the body still scrolls as a
 * fallback when the result fields expand or on short screens).
 *
 * PTV2-1301: the dialog guard intercepts Escape / outside-click / the X so a
 * dirty draft raises Save / Discard / Keep editing instead of being silently
 * dropped (a modal can't reach the section-nav blocker; only its own dismiss or
 * a refresh can, and both route through the guard here).
 */
export function TestOrderFormDialog(props: TestOrderFormDialogProps): ReactElement {
  const { t } = useTranslation();
  const focusRef = useFocusOnMount<HTMLInputElement>();
  const state = useTestOrderFormState(props);
  const isEdit = props.mode === 'edit';
  const title = isEdit
    ? t('patient.testOrders.editOrder')
    : t('patient.testOrders.newOrder');

  const guard = useDirtyDialogGuard({
    dirty: state.dirty,
    canSave: state.canSubmit,
    saving: state.pending,
    label: title,
    onSave: state.submit,
    onClose: props.onClose,
    // PTV2-1327: the draft registers via useEditableDraft (in useTestOrderFormState); the guard only
    // intercepts dismissal here, so it must not double-register.
    register: false,
  });

  return (
    <>
      <Dialog open {...guard.rootProps}>
        <DialogContent
          className="sm:max-w-3xl max-h-[calc(100vh-3rem)] p-0 gap-0 flex flex-col"
          {...guard.contentProps}
        >
          <DialogHeader className="px-6 pt-6 pb-3 border-b border-border shrink-0">
            <DialogTitle>{title}</DialogTitle>
          </DialogHeader>
          <TestOrderFormView
            state={state}
            focusRef={focusRef}
            lockStatusToCancelled={isEdit && props.order.status === 'CANCELLED'}
            submitLabel={
              isEdit
                ? t('patient.testOrders.save')
                : t('patient.testOrders.recordOrder')
            }
            onClose={guard.requestClose}
          />
        </DialogContent>
      </Dialog>
      {guard.confirm}
    </>
  );
}
